export type ClientErrorSource =
  | "error-boundary"
  | "window-error"
  | "unhandled-rejection"
  | "cart-action"

export type ClientErrorPayload = {
  source: ClientErrorSource
  name?: string
  message: string
  stack?: string
  digest?: string
  path?: string
  userAgent?: string
  extra?: Record<string, string | number | boolean | null>
  timestamp: string
}

type BuildPayloadArgs = {
  source: ClientErrorSource
  error: unknown
  path?: string
  userAgent?: string
  extra?: Record<string, unknown>
}

const SAFE_EXTRA_KEYS = new Set([
  "cartId",
  "countryCode",
  "digest",
  "productHandle",
  "productId",
  "selectedVariantId",
  "variantSku",
])

const asString = (value: unknown) =>
  typeof value === "string" ? value : value == null ? "" : String(value)

const limit = (value: unknown, max: number) => {
  const text = asString(value)
  return text.length > max ? text.slice(0, max) : text
}

const readErrorField = (error: unknown, field: string) => {
  if (error && typeof error === "object" && field in error) {
    return (error as Record<string, unknown>)[field]
  }
  return undefined
}

const sanitizeExtra = (extra?: Record<string, unknown>) => {
  if (!extra) {
    return undefined
  }

  const sanitized: Record<string, string | number | boolean | null> = {}

  for (const [key, value] of Object.entries(extra)) {
    if (!SAFE_EXTRA_KEYS.has(key)) {
      continue
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null
    ) {
      sanitized[key] = typeof value === "string" ? limit(value, 160) : value
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined
}

export const buildClientErrorPayload = ({
  source,
  error,
  path,
  userAgent,
  extra,
}: BuildPayloadArgs): ClientErrorPayload => {
  const name = limit(readErrorField(error, "name"), 120) || undefined
  const message =
    limit(readErrorField(error, "message") || error || "Unknown client error", 240) ||
    "Unknown client error"
  const stack = limit(readErrorField(error, "stack"), 2000) || undefined
  const digest = limit(readErrorField(error, "digest") || extra?.digest, 120) || undefined

  return {
    source,
    ...(name ? { name } : {}),
    message,
    ...(stack ? { stack } : {}),
    ...(digest ? { digest } : {}),
    ...(path ? { path: limit(path, 300) } : {}),
    ...(userAgent ? { userAgent: limit(userAgent, 300) } : {}),
    ...(sanitizeExtra(extra) ? { extra: sanitizeExtra(extra) } : {}),
    timestamp: new Date().toISOString(),
  }
}

// Recoverable React hydration mismatches: the server HTML didn't match the client
// at hydration time, so React discards that subtree and re-renders it — the page
// keeps working. In production these are overwhelmingly caused by client-side DOM
// mutation we cannot control (in-app/mobile browser auto-translation, iOS
// data-detector auto-linking of numbers, content blockers/extensions), not by our
// own render. They were flooding [client-error] logs (hundreds/day, ~74% iPhone)
// and burying genuine failures.
const RECOVERABLE_REACT_HYDRATION = /Minified React error #(418|421|422|423|425)\b/

// A stack with real frames always spans multiple lines — one per frame
// ("\n    at fn (file:line)" in V8, "\n fn@file:line" in Firefox). A frameless
// stack is a single header line ("SyntaxError: <message>") with nothing to act on.
const hasNoStackFrames = (stack?: string): boolean =>
  !stack || !stack.includes("\n")

// True for low/zero-signal client errors we deliberately do NOT report. Real UI
// breakage still surfaces: it arrives via the "error-boundary" source (a component
// actually threw), which is never filtered here.
export const isIgnorableClientError = ({
  source,
  name,
  message,
  stack,
}: {
  source: ClientErrorSource
  name?: string
  message: string
  stack?: string
}): boolean => {
  // Opaque cross-origin "Script error." — no stack/file, nothing actionable.
  if (/^script error\.?$/i.test(message.trim())) {
    return true
  }
  // Facebook/Instagram WKWebView bridge noise.
  if (/Java object is gone|window\.webkit\.messageHandlers/i.test(message)) {
    return true
  }
  // Recoverable hydration warnings caught by the passive global listeners only.
  if (
    (source === "window-error" || source === "unhandled-rejection") &&
    RECOVERABLE_REACT_HYDRATION.test(message)
  ) {
    return true
  }
  // Frameless script-parse errors from embedded/in-app browsers (Facebook /
  // Instagram WebViews injecting or truncating their own bridge scripts): a
  // global SyntaxError whose stack is a single header line — no frames, no
  // source file (e.g. "Unexpected end of input" on Android FB4A). A genuine
  // parse failure in our own code carries stack frames and still reports. Same
  // family as "Script error." above.
  if (
    (source === "window-error" || source === "unhandled-rejection") &&
    name === "SyntaxError" &&
    hasNoStackFrames(stack)
  ) {
    return true
  }
  return false
}

export const reportClientError = async (args: BuildPayloadArgs) => {
  if (typeof window === "undefined") {
    return
  }

  const payload = buildClientErrorPayload({
    ...args,
    path: args.path || window.location.pathname,
    userAgent: args.userAgent || window.navigator.userAgent,
  })

  if (
    isIgnorableClientError({
      source: payload.source,
      name: payload.name,
      message: payload.message,
      stack: payload.stack,
    })
  ) {
    return
  }

  try {
    await fetch("/api/client-error", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
  } catch {
    // Error reporting must never create another customer-facing error.
  }
}
